import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, AlertCircle, Loader2, Plus, Edit2, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { QuestionItem, QuestionOption, QuestionSet } from '../../types';
import { getAuthHeaders, customFetch } from '../../services/apiService';
import { MediaGalleryModal } from './MediaGalleryModal';

interface ManualQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetSet: QuestionSet;
  editingQuestion?: QuestionItem | null;
  onSuccess: (savedQuestion: QuestionItem) => void;
  onQuotaExceeded?: (message?: string) => void;
}

export const ManualQuestionModal: React.FC<ManualQuestionModalProps> = ({
  isOpen,
  onClose,
  targetSet,
  editingQuestion,
  onSuccess,
  onQuotaExceeded
}) => {
  const [questionText, setQuestionText] = useState('');
  const [questionMedia, setQuestionMedia] = useState<string | null>(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

  const [options, setOptions] = useState<QuestionOption[]>([
    { id: 'A', text: '', isCorrect: true },
    { id: 'B', text: '', isCorrect: false },
    { id: 'C', text: '', isCorrect: false },
    { id: 'D', text: '', isCorrect: false }
  ]);
  const [explanation, setExplanation] = useState('');
  const [difficulty, setDifficulty] = useState('Thông hiểu');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingQuestion) {
      setQuestionText(editingQuestion.questionText);
      setQuestionMedia(editingQuestion.questionMedia || null);
      setOptions(
        editingQuestion.options && editingQuestion.options.length >= 2
          ? editingQuestion.options.map(o => ({ ...o }))
          : [
              { id: 'A', text: '', isCorrect: true },
              { id: 'B', text: '', isCorrect: false },
              { id: 'C', text: '', isCorrect: false },
              { id: 'D', text: '', isCorrect: false }
            ]
      );
      setExplanation(editingQuestion.explanation || '');
      setDifficulty(editingQuestion.difficulty || 'Thông hiểu');
    } else {
      setQuestionText('');
      setQuestionMedia(null);
      setOptions([
        { id: 'A', text: '', isCorrect: true },
        { id: 'B', text: '', isCorrect: false },
        { id: 'C', text: '', isCorrect: false },
        { id: 'D', text: '', isCorrect: false }
      ]);
      setExplanation('');
      setDifficulty('Thông hiểu');
    }
    setError(null);
  }, [editingQuestion, isOpen]);

  if (!isOpen) return null;

  const handleOptionTextChange = (index: number, text: string) => {
    const updated = [...options];
    updated[index].text = text;
    setOptions(updated);
  };

  const handleSelectCorrect = (index: number) => {
    const updated = options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index
    }));
    setOptions(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionText.trim()) {
      setError('Vui lòng nhập nội dung câu hỏi.');
      return;
    }

    if (options.some(o => !o.text.trim())) {
      setError('Vui lòng điền đầy đủ nội dung cho tất cả 4 phương án.');
      return;
    }

    if (!options.some(o => o.isCorrect)) {
      setError('Vui lòng chọn 1 phương án là đáp án đúng.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let questionId = '';
      if (editingQuestion) {
        questionId = editingQuestion._id || editingQuestion.id || '';
        if (!questionId) {
          setError('Không tìm thấy ID câu hỏi để cập nhật.');
          setLoading(false);
          return;
        }
      }

      const url = editingQuestion ? `/api/v1/questions/${questionId}` : '/api/v1/questions/manual';
      const method = editingQuestion ? 'PUT' : 'POST';

      const res = await customFetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          setId: targetSet.id,
          questionText: questionText.trim(),
          questionMedia,
          options,
          explanation: explanation.trim(),
          difficulty
        })
      });

      const data = await res.json().catch(() => ({}));
      
      // Bắt lỗi 403 Forbidden khi vượt hạn mức số lượng câu hỏi trong bộ
      if (res.status === 403 || data.isQuotaExceeded) {
        const errorMsg = data.message || 'Bộ câu hỏi đã đạt giới hạn số lượng câu hỏi tối đa cho phép của gói hiện tại.';
        if (onQuotaExceeded) {
          onClose();
          onQuotaExceeded(errorMsg);
          return;
        }
        throw new Error(errorMsg);
      }

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Không thể lưu câu hỏi.');
      }

      onSuccess(data.question);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Đã xảy ra lỗi khi lưu câu hỏi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#236539] flex items-center justify-center font-bold">
            {editingQuestion ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {editingQuestion ? 'Chỉnh sửa câu hỏi' : 'Thêm câu hỏi mới'}
            </h3>
            <p className="text-xs text-slate-500">
              Bộ: <span className="font-semibold text-slate-700">{targetSet.name}</span> ({targetSet.subject} - Lớp {targetSet.grade})
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Question Text */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex justify-between items-center">
              <span>Nội dung câu hỏi <span className="text-rose-500">*</span></span>
            </label>
            <div className="relative">
              <textarea
                required
                rows={3}
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Nhập nội dung câu hỏi trắc nghiệm..."
                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 pb-12 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white transition-all"
              />
              <div className="absolute bottom-3 left-3 flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setIsMediaModalOpen(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium transition-colors border border-slate-200"
                >
                  <ImageIcon className="w-3.5 h-3.5 text-[#236539]" />
                  <span>{questionMedia ? 'Thay ảnh' : 'Thêm ảnh đính kèm'}</span>
                </button>
              </div>
            </div>
            
            {questionMedia && (
              <div className="mt-3 relative inline-block group">
                <img 
                  src={questionMedia} 
                  alt="Ảnh đính kèm" 
                  className="h-[150px] object-cover rounded-xl border-2 border-slate-200"
                />
                <button
                  type="button"
                  onClick={() => setQuestionMedia(null)}
                  className="absolute -top-2 -right-2 bg-white text-rose-500 p-1.5 rounded-full shadow-md border border-slate-100 hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100"
                  title="Xóa ảnh"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Options */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center justify-between">
              <span>Các phương án đáp án (Nhấn vào chữ cái để chọn đáp án đúng) <span className="text-rose-500">*</span></span>
            </label>
            <div className="space-y-2.5">
              {options.map((opt, idx) => {
                const optKey = opt.key || (opt.id && opt.id.length === 1 ? opt.id : String.fromCharCode(65 + idx));
                return (
                <div
                  key={opt.id || idx}
                  className={`p-2.5 rounded-xl border flex items-center space-x-3 transition-all ${
                    opt.isCorrect
                      ? 'bg-emerald-50/80 border-emerald-400 ring-2 ring-emerald-500/20'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectCorrect(idx)}
                    title={opt.isCorrect ? 'Đáp án ĐÚNG (Bấm để đổi)' : 'Bấm để đặt làm đáp án ĐÚNG'}
                    className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center shrink-0 transition-all ${
                      opt.isCorrect
                        ? 'bg-[#236539] text-white shadow-xs scale-105'
                        : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    {optKey}
                  </button>

                  <input
                    type="text"
                    required
                    value={opt.text}
                    onChange={(e) => handleOptionTextChange(idx, e.target.value)}
                    placeholder={`Nội dung đáp án ${optKey}...`}
                    className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
                  />

                  {opt.isCorrect && (
                    <div className="flex items-center text-xs font-bold text-emerald-700 space-x-1 shrink-0">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Đáp án đúng</span>
                    </div>
                  )}
                </div>
              )})}
            </div>
          </div>

          {/* Difficulty & Explanation */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Mức độ tư duy
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539]"
              >
                <option value="Nhận biết">Nhận biết</option>
                <option value="Thông hiểu">Thông hiểu</option>
                <option value="Vận dụng">Vận dụng</option>
                <option value="Vận dụng cao">Vận dụng cao</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Lời giải thích / Hướng dẫn giải (Tùy chọn)
              </label>
              <input
                type="text"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Giải thích vì sao đáp án này đúng..."
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539]"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end space-x-2.5 pt-3 border-t border-slate-100">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#236539] hover:bg-[#1b4f2c] text-white shadow-md flex items-center space-x-1.5"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <span>{editingQuestion ? 'Cập nhật câu hỏi' : 'Lưu câu hỏi'}</span>
            </Button>
          </div>
        </form>
      </div>

      <MediaGalleryModal 
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        onSelect={(url) => setQuestionMedia(url)}
      />
    </div>
  );
};
